import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Page, Group, Layout, Button, Grid, Table, ExcelUpload, Tab, FormControl } from "@/comn/components";
import { comnEnvs, comnUtils } from "@/comn/utils";
import { lpcoEnvs, lpcoUtils } from "@/tz/go/egaz/osci/lpco/comn";
import {
    useForm,
    useFetch,
    useGrid,
    useResource,
    usePopup,
    useStore,
    useToast,
    useAuth,
    useModal,
    useTab,
    usePage,
} from "@/comn/hooks";
import {
    BASE,
    URLS,
    APIS,
    SF_SCHEMA_INSC_FORM,
    SF_SCHEMA_TANSAD_FORM,
    SG_GRID_CNTR_LIST,
    SG_GRID_JNT_INSC_APRV_LIST,
} from "@/tz/go/egaz/osci/lpco/jntinsc/services/OsciJntinscService";
import { OSCI0301003S } from "@/tz/go/egaz/osci/lpco/jntinsc/OSCI0301003S";

/*
 * Details Joint Inspection
 * !== 합동검사 대상 상세 화면 ==!
 */
export const OSCI0301002S = (props: any) => {
    /**
     * Page Unique identifier !== 화면 고유 식별자 ==!
     * Page Title !== 페이지 타이틀 ==!
     */
    const { pgeUid, menuId } = usePage();
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!

    const { tansadNo } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!

    const location = useLocation();
    const param = { ...location.state };

    const [usrVisible, setUsrVisible] = useState(false);
    const [rcveVisible, setRcveVisible] = useState(false);
    const [vrfnVisible, setVrfnVisible] = useState(false);

    const { getResource, updateResource } = useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "OSC0165" } /* Inspection Reserve Status Code */,
            { area: "wrhsCd" } /* Warehouse Code */,
            { area: "orgCd" } /* Organization Code !== 기관_코드 ==! */,
            { area: "comnCd", comnCd: "OSC0042" } /* Regime General Procedure Code */,
            { area: "comnCd", comnCd: "OSC0033" } /* Selectivity Result Code */,
            { area: "comnCd", comnCd: "OSC0006" } /* Loading Type Code !== 적재_유형_코드 ==! */,
            { area: "comnCd", comnCd: "OSC0004" } /* Continer Size Code !== 컨테이너_크기_코드 ==! */,
            { area: "comnCd", comnCd: "OSC0005" } /* Freight Indicator Code !== 운임_지표_코드 ==! */,
            { area: "comnCd", comnCd: "OSC0080" } /* Package Unit Code !== 포장_단위_코드 ==! */,
            { area: "comnCd", comnCd: "OSC0079" } /* Weight Unit Code !== 중량_단위_코드 ==! */,
        ],
    });

    const [prcsStat, setPrcsStat] = useState("");
    const [blData, setBlData] = useState(comnUtils.getGridData([]));
    const [cntrData, setCntrData] = useState([]);
    const [jntInscCntrData, setJntInscCntrData] = useState<any[]>([]);
    const [pckgTpCd, setPckgTpCd] = useState("");
    const [tansadExists, setTansadExists] = useState(true);

    const tab = useTab({
        defaultSchema: {
            id: "tab",
            schema: [{ label: "T_TANSAD_GNRL" }, { label: "T_APRV_HIST" }],
        },
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Joint Inspection Booking Form !== 합동 검사 예약 상세 Form ==!
        inscForm: useForm({
            defaultSchema: SF_SCHEMA_INSC_FORM,
            defaultValues: {},
        }),
        tansadForm: useForm({
            defaultSchema: SF_SCHEMA_TANSAD_FORM,
            defaultValues: {},
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        cntrList: useGrid({
            defaultSchema: SG_GRID_CNTR_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
        // Approval History !== 승인 이력 ==!
        jntInscAprvList: useGrid({
            defaultSchema: SG_GRID_JNT_INSC_APRV_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    const render = {
        grid_jntInscAprvList: {
            head: {},
            cell: {
                orgNm: (props: any) => {
                    const { binding, rowValues, value } = props;
                    let rsltTxt = "";

                    if (rowValues.orgCd.startsWith("OGA")) {
                        getResource("orgCd")?.forEach((item: any) => {
                            if (item.orgCd === rowValues.orgCd) {
                                rsltTxt = "[" + item.orgCd + "] " + item.orgNm;
                            }
                        });
                    } else {
                        getResource("wrhsCd")?.forEach((item: any) => {
                            if (item.coDclaCd === rowValues.orgCd) {
                                rsltTxt = "[" + item.coDclaCd + "] " + item.wrhsNm;
                            }
                        });
                    }

                    return rsltTxt;
                },
            },
        },
    };

    const fetch = {
        getJntInscBkngDtl: useFetch({
            api: (data) => APIS.getJntInscBkngDtl(data),
            onSuccess: (data) => {
                // 합동검사 예약 정보 처리
                if (!data.result.content.jntInscBkngInfo) {
                    // no data
                    modal.openModal({
                        content: "msg.osc.00050",
                        onCancel: () => {
                            navigate(URLS.osci0301001q);
                        },
                    });
                } else {
                    form.inscForm.setValues(data.result.content.jntInscBkngInfo);
                    form.inscForm.setValue("cstmOfceCd", data.result.content.jntInscBkngInfo.tansadNo.substring(0, 4));
                    form.inscForm.setValue("tansadYy", data.result.content.jntInscBkngInfo.tansadNo.substring(4, 6));
                    form.inscForm.setValue("tansadSrno", data.result.content.jntInscBkngInfo.tansadNo.substring(6, 13));

                    if (data.result.content.jntInscBkngInfo.mdfyGodsLoctWrhsCd != null) {
                        form.inscForm.setValue(
                            "godsLoctWrhsCd",
                            data.result.content.jntInscBkngInfo.mdfyGodsLoctWrhsCd,
                        );
                    }

                    let prcsStatCd = data.result.content.jntInscBkngInfo.prcsStatCd;
                    setPrcsStat(prcsStatCd);

                    if (data.result.content.jntInscBkngInfo.asnmtOfcrId == auth.get("usrId")) {
                        setUsrVisible(true);
                    }

                    if (
                        data.result.content.jntInscBkngInfo.asnmtOfcrId == auth.get("usrId") &&
                        data.result.content.jntInscBkngInfo.asinDtm != null &&
                        prcsStatCd != "C" &&
                        data.result.content.jntInscBkngInfo.ofcrPrcssDtm == null
                    ) {
                        setVrfnVisible(true);
                    }

                    if (data.result.content.jntInscBkngInfo.prcsStatCd == "A") {
                        setRcveVisible(true);
                    }

                    form.inscForm.setValue("aprvDt", data.result.content.jntInscBkngInfo.aprvDtm);
                }
                // TANSAD 정보 처리
                setTansadExists(!!data.result.content.tansadInfo);
                if (data.result.content.tansadInfo) {
                    form.tansadForm.setValues(data.result.content.tansadInfo);
                }
                // Master/House BL 정보 처리
                if (data.result.content.blInfo) {
                    setBlData(data.result.content.blInfo);
                    setPckgTpCd(data.result.content.blInfo.pckgTpCd);
                }
                // Container List 정보 처리
                if (data.result.content.cntrInfo) {
                    const cntrInfo = data.result.content.cntrInfo;
                    setCntrData(data.result.content.cntrInfo);
                    grid.cntrList.setData(comnUtils.getGridData(data.result.content.cntrInfo));
                    if (data.result.content.jntInscCntrInfo && data.result.content.jntInscCntrInfo.length > 0) {
                        setJntInscCntrData(data.result.content.jntInscCntrInfo);

                        grid.cntrList.setOption("checkbox", "readOnly");
                    }
                }
            },
            onError: (error) => {},
        }),
        updateReceived: useFetch({
            api: (tansadNo, prcsStatCd) => {
                return APIS.updateReceived(tansadNo, prcsStatCd);
            },
            onSuccess: (data, param) => {
                window.location.reload();
            },
            onError: (err) => {
                modal.openModal({ content: err.response.data.errorMessage });
            },
        }),
        getJntInscPrcsHistList: useFetch({
            api: (tansadNo) => APIS.getJntInscPrcsHistList(tansadNo),
            onSuccess: (data) => {
                if (data.aprvHistList.content!) {
                    console.log(data.aprvHistList.content);
                    let prcssStatCd = "";
                    if (data.aprvHistList.content.length > 0) {
                        for (let i = 0; i < data.aprvHistList.content.length; i++) {
                            prcssStatCd = data.aprvHistList.content[i].prcssStatCd;
                            switch (prcssStatCd) {
                                case "AP":
                                    data.aprvHistList.content[i].prcssRsnNm = t("L_CNDT_APRV");
                                    data.aprvHistList.content[i].prcssCn = t("msg.osc.00139");
                                    break;
                                default:
                                    data.aprvHistList.content[i].prcssRsnNm = t("L_RSN");
                                    // data.aprvHistList.content[i].prcssCn = "";
                                    break;
                            }

                            data.aprvHistList.content[i].aprvRmrk = t("L_RMRK");
                        }
                        grid.jntInscAprvList.setData(comnUtils.getGridData(data.aprvHistList.content || []));
                    }
                }
            },
            onError: (error) => {
                modal.openModal({
                    content: "msg.osc.00015",
                });
            },
        }),
    };

    const handler = {
        setInit: async () => {
            form.tansadForm.setEditable(false);
            grid.cntrList.setOption("edit", false);
            handler.getJntInscBkngDtl(tansadNo);
        },
        getJntInscBkngDtl: (tansadNo: any) => {
            fetch.getJntInscBkngDtl.fetch(tansadNo);
        },
        updateReceived: (tansadNo: any, prcsStatCd: any) => {
            fetch.updateReceived.fetch(tansadNo, prcsStatCd);
        },
        changeTab: (index: any) => {
            if (index === 0) {
                handler.setInit();
            } else if (index === 1) {
                handler.getJntInscPrcsHistList(tansadNo);
            }
        },
        getJntInscPrcsHistList: async (tansadNo: any) => {
            fetch.getJntInscPrcsHistList.fetch(tansadNo);
        },
    };

    /*
     * Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.setInit();
    }, []);

    return (
        <Page>
            {/* <form> */}
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Label {...form.inscForm.schema.tansadNo} />
                            <Group.Field
                                as="link"
                                onClick={() => {
                                    modal.openModal({
                                        url: `${lpcoEnvs.base}/lpco/tansad/osci0202002q/${form.inscForm.getValue(
                                            "cstmOfceCd",
                                        )}-${form.inscForm.getValue("tansadYy")}-${form.inscForm.getValue(
                                            "tansadSrno",
                                        )}`,
                                        draggable: true,
                                        callback: (data) => {
                                            modal.closeModal();
                                        },
                                    });
                                }}
                            >
                                <Group.Control {...form.inscForm.schema.cstmOfceCd} />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.inscForm.schema.tansadYy} />
                                <Group.Any>-</Group.Any>
                                <Group.Control {...form.inscForm.schema.tansadSrno} />
                            </Group.Field>
                            <Group.Control {...form.inscForm.schema.prcsStatCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.inscForm.schema.blNo} />
                            <Group.Control {...form.inscForm.schema.godsLoctWrhsCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.inscForm.schema.cntrCnt} />
                            <Group.Control {...form.inscForm.schema.cntrLstCn} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.inscForm.schema.bkngRqstDt} />
                            <Group.Control {...form.inscForm.schema.bkngMdfyRqstDt} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Label {...form.inscForm.schema.appNo} direction="row" />
                            <Group.Field>
                                {form.inscForm.getValue("appId") != null &&
                                    (() => {
                                        const arrAppUuid = (form.inscForm.getValue("appId") as string).split(",");
                                        const arrAppNo = (form.inscForm.getValue("appNo") as string).split(",");

                                        return (
                                            <Layout direction="col">
                                                {arrAppNo.map((appNo: string, idx: number) => {
                                                    const appId = arrAppUuid[idx];
                                                    return (
                                                        <>
                                                            <FormControl
                                                                {...form.inscForm.schema.appNo}
                                                                value={appNo}
                                                                edit={false}
                                                                as="link"
                                                                onClick={() => {
                                                                    modal.openModal({
                                                                        url: `${comnEnvs.url.osci}/osci/lpco/app/new/osci0201002s/${appId}`,
                                                                        draggable: true,
                                                                        callback: () => modal.closeModal(),
                                                                    });
                                                                }}
                                                            />
                                                        </>
                                                    );
                                                })}
                                            </Layout>
                                        );
                                    })()}
                            </Group.Field>
                            <Group.Control {...form.inscForm.schema.ucrNo} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.inscForm.schema.jntInscZoneAddr} />
                        </Group.Row>
                        {prcsStat !== "A" && (
                            <>
                                {prcsStat === "B" && (
                                    <Group.Row>
                                        <Group.Control {...form.inscForm.schema.bkngCfrmDtm} />
                                    </Group.Row>
                                )}
                                {prcsStat === "C" && (
                                    <>
                                        <Group.Row>
                                            <Group.Control {...form.inscForm.schema.bkngAprvDtm} />
                                            <Group.Control {...form.inscForm.schema.inscCfrmDt} />
                                        </Group.Row>
                                        <Group.Row>
                                            <Group.Control {...form.inscForm.schema.apvrNm} />
                                            <Group.Control {...form.inscForm.schema.aprvDt} />
                                        </Group.Row>
                                    </>
                                )}
                            </>
                        )}
                    </Group.Section>

                    <Layout direction="row">
                        <Layout.Left>
                            <Button
                                role="list"
                                onClick={() => {
                                    navigate(URLS.osci0301001q);
                                }}
                            />
                        </Layout.Left>
                        <Layout.Right>
                            {auth.get("authList")?.includes("auth_osci0011") &&
                                (prcsStat === "I" || prcsStat === "E") && (
                                    <Button
                                        type="button"
                                        onClick={() => {
                                            modal.openModal({
                                                content: (
                                                    <OSCI0301003S
                                                        viewOnly={true}
                                                        paramData={{
                                                            tansadNo: tansadNo,
                                                            cstmOfceCd: form.inscForm.getValue("cstmOfceCd"),
                                                            tansadYy: form.inscForm.getValue("tansadYy"),
                                                            tansadSrno: form.inscForm.getValue("tansadSrno"),
                                                            reffNo:
                                                                form.inscForm.getValue("reffTin") +
                                                                form.inscForm.getValue("reffYy") +
                                                                form.inscForm.getValue("reffSrno"),
                                                            reffTin: form.inscForm.getValue("reffTin"),
                                                            reffYy: form.inscForm.getValue("reffYy"),
                                                            reffSrno: form.inscForm.getValue("reffSrno"),
                                                            bkngRqstDt: form.inscForm.getValue("bkngRqstDt"),
                                                            blNo: form.inscForm.getValue("blNo"),
                                                            prcsStatCd: form.inscForm.getValue("prcsStatCd"),
                                                        }}
                                                        callbackFn={() => {
                                                            modal.closeModal();
                                                        }}
                                                    />
                                                ),
                                                size: "lg",
                                                draggable: true,
                                            });
                                        }}
                                    >
                                        {t("B_DTL")}
                                    </Button>
                                )}
                            {auth.get("authList")?.includes("auth_osci0011") && usrVisible && rcveVisible && (
                                <>
                                    <Button
                                        type="button"
                                        color="success"
                                        onClick={() => {
                                            handler.updateReceived(tansadNo, prcsStat);
                                        }}
                                    >
                                        {t("L_RCVE_1")}
                                    </Button>
                                </>
                            )}
                            {auth.get("authList")?.includes("auth_osci0011") && usrVisible && vrfnVisible && (
                                <>
                                    <Button
                                        variant="primary"
                                        onClick={() => {
                                            modal.openModal({
                                                content: (
                                                    <OSCI0301003S
                                                        paramData={{
                                                            tansadNo: tansadNo,
                                                            cstmOfceCd: form.inscForm.getValue("cstmOfceCd"),
                                                            tansadYy: form.inscForm.getValue("tansadYy"),
                                                            tansadSrno: form.inscForm.getValue("tansadSrno"),
                                                            reffNo:
                                                                form.inscForm.getValue("reffTin") +
                                                                form.inscForm.getValue("reffYy") +
                                                                form.inscForm.getValue("reffSrno"),
                                                            reffTin: form.inscForm.getValue("reffTin"),
                                                            reffYy: form.inscForm.getValue("reffYy"),
                                                            reffSrno: form.inscForm.getValue("reffSrno"),
                                                            bkngRqstDt: form.inscForm.getValue("bkngRqstDt"),
                                                            blNo: form.inscForm.getValue("blNo"),
                                                            prcsStatCd: form.inscForm.getValue("prcsStatCd"),
                                                        }}
                                                        callbackFn={(data: any) => {
                                                            modal.closeModal();
                                                            window.location.reload();
                                                        }}
                                                    />
                                                ),
                                                size: "lg",
                                                draggable: true,
                                            });
                                        }}
                                    >
                                        {t("B_BOOK_RSLT_RGSR")}
                                    </Button>
                                </>
                            )}
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Tab
                        {...tab.tab}
                        onChange={(index) => {
                            handler.changeTab(index);
                        }}
                    >
                        {/* TANSAD 상세정보 및 컨테이너 정보 */}
                        <Tab.Panel>
                            <Layout direction="col">
                                {/* Pre-verification bookings come before the TANSAD, so say why the details are empty
                                    !== 사전검증 예약은 TANSAD보다 먼저 들어오므로 신고 상세가 비어 있는 이유를 알려 준다 ==! */}
                                <Layout valign="center" gap={2}>
                                    <Group.Title title={"T_TANSAD_DCLR_DTL"} titleSize={1} />
                                    {!tansadExists && <span className="text-sm text-error">{t("msg.osc.00339")}</span>}
                                </Layout>
                                <Group.Section>
                                    <Group.Row>
                                        <Group.Control {...form.tansadForm.schema.decMode} />
                                        <Group.Control {...form.tansadForm.schema.sltvRsltCd} />
                                    </Group.Row>
                                    <Group.Row>
                                        <Group.Control {...form.tansadForm.schema.tin} />
                                        <Group.Control {...form.tansadForm.schema.dcltTin} />
                                    </Group.Row>
                                    <Group.Row>
                                        <Group.Control
                                            // as="link"
                                            {...form.tansadForm.schema.crn}
                                            // onClick={() => {
                                            //     modal.openModal({
                                            //         url: `${
                                            //             comnEnvs.url.osci
                                            //         }/mnfs/imp/sea/CGME0101027Q/${form.tansadForm.getValue("crn")}`,
                                            //         draggable: true,
                                            //         callback: (data) => {
                                            //             modal.closeModal();
                                            //         },
                                            //     });
                                            // }}
                                        />
                                        <Group.Control {...form.tansadForm.schema.godsLoctReffCn} />
                                    </Group.Row>
                                </Group.Section>

                                {blData &&
                                    Array.isArray(cntrData) &&
                                    cntrData.length > 0 &&
                                    pckgTpCd !== "B" &&
                                    pckgTpCd !== "L" && (
                                        <>
                                            <Layout direction="row">
                                                <Group.Section>
                                                    <Group.Header>
                                                        Total Containers/Vehicles available for Inspection :{" "}
                                                        {cntrData.length}
                                                    </Group.Header>
                                                    <Grid
                                                        {...grid.cntrList.grid}
                                                        defaultCheck={(data: any) => {
                                                            for (let i = 0; i < jntInscCntrData.length; i++) {
                                                                if (data.CNTR_NO === jntInscCntrData[i].cntrNo) {
                                                                    return true;
                                                                }
                                                            }
                                                        }}
                                                    />
                                                </Group.Section>
                                            </Layout>
                                        </>
                                    )}
                            </Layout>
                        </Tab.Panel>
                        {/* 처리 결과 */}
                        <Tab.Panel>
                            <Layout direction="col">
                                <Layout>
                                    <Layout.Left>
                                        <Group.Title title={"T_APRV_HIST"} titleSize={1}></Group.Title>
                                    </Layout.Left>
                                </Layout>

                                <Group.Section>
                                    <Grid {...grid.jntInscAprvList.grid} render={render.grid_jntInscAprvList} />
                                </Group.Section>
                            </Layout>
                        </Tab.Panel>
                    </Tab>
                </Group.Body>
            </Group>
        </Page>
    );
};
